// ============================================================
// saveBaseVersion.ts
// Optimistic-concurrency baseline: the SERVER updatedAt a record had
// when the frontend last loaded (or last successfully saved) it, keyed
// by record id. The autosave hooks send this as `baseUpdatedAt` on save
// so the backend can 409 if someone else wrote the record in between.
//
// CRITICAL: this must be the SERVER value captured from the raw fetch
// response (setCurrentCaseById / setSelectedClient) or advanced from the
// save response — NOT currentCase.updatedAt, which the store stamps
// locally on every edit (useCasevoStore.updateCase) and would defeat the
// guard.
//
// Module-scope maps (not React state): read/written outside React by the
// autosave singletons and the store. A separate module keeps a clean
// dependency edge — both the store and the hooks import from here without
// importing each other.
// ============================================================
const caseBase = new Map<string, string>()
const clientBase = new Map<string, string>()
// Satellite records (per-row lists loaded with the case): keyed by the
// record's own id. Captured when the list loads (setCurrentCaseById) and
// advanced on each successful per-row save.
const witnessBase = new Map<string, string>()
const deadlineBase = new Map<string, string>()

export const setCaseBaseUpdatedAt = (id: string, updatedAt: string): void => {
  caseBase.set(id, updatedAt)
}
export const getCaseBaseUpdatedAt = (id: string): string | null =>
  caseBase.get(id) ?? null

// `reason` tags the caller (load / notes-success / fields-success / 409 paths /
// remote-apply). Kept as a harmless diagnostic label — no runtime effect.
export const setClientBaseUpdatedAt = (id: string, updatedAt: string, reason: string = '?'): void => {
  void reason
  clientBase.set(id, updatedAt)
}
export const getClientBaseUpdatedAt = (id: string): string | null =>
  clientBase.get(id) ?? null

export const setWitnessBaseUpdatedAt = (id: string, updatedAt: string): void => {
  witnessBase.set(id, updatedAt)
}
export const getWitnessBaseUpdatedAt = (id: string): string | null =>
  witnessBase.get(id) ?? null

export const setDeadlineBaseUpdatedAt = (id: string, updatedAt: string): void => {
  deadlineBase.set(id, updatedAt)
}
export const getDeadlineBaseUpdatedAt = (id: string): string | null =>
  deadlineBase.get(id) ?? null

// Tasks (per-row, like witnesses/deadlines). Captured when the task edit form
// opens (from editingTask.updatedAt) and advanced on each successful per-field
// autosave. Enables the task update endpoint's optimistic-concurrency guard.
const taskBase = new Map<string, string>()
export const setTaskBaseUpdatedAt = (id: string, updatedAt: string): void => {
  taskBase.set(id, updatedAt)
}
export const getTaskBaseUpdatedAt = (id: string): string | null =>
  taskBase.get(id) ?? null

// ── Per-row BASE VALUES snapshots (COLUMN-AWARE 409 recovery) ─────────
// Parallel to the base-updatedAt maps above: the column VALUES the client last
// SAW for a row — captured at the same moments the base updatedAt advances (list
// load, row creation, the task edit form opening, a successful per-row save, and
// a remote apply). On a 409 the satellite autosaves (witness/deadline/task)
// compare THIS save's touched columns' current SERVER values against these base
// values (via reconcile409.serverChangedTouchedColumns) to distinguish a
// SPURIOUS cross-column 409 (another user edited different columns → safe to
// silently re-apply) from a GENUINE same-column clash (surface the conflict).
// This is the satellite equivalent of useAutoSave's lastSavedFlat. Stored as the
// row's flat API shape (a shallow copy so later store mutations don't bleed in).
// Captured only at server-truth moments — NEVER mid-edit — so the snapshot is
// always "what the user started from", not their in-flight change.
const witnessBaseValues = new Map<string, Record<string, unknown>>()
const deadlineBaseValues = new Map<string, Record<string, unknown>>()
const taskBaseValues = new Map<string, Record<string, unknown>>()

export const setWitnessBaseValues = (id: string, values: Record<string, unknown>): void => {
  witnessBaseValues.set(id, { ...values })
}
export const getWitnessBaseValues = (id: string): Record<string, unknown> | null =>
  witnessBaseValues.get(id) ?? null

export const setDeadlineBaseValues = (id: string, values: Record<string, unknown>): void => {
  deadlineBaseValues.set(id, { ...values })
}
export const getDeadlineBaseValues = (id: string): Record<string, unknown> | null =>
  deadlineBaseValues.get(id) ?? null

export const setTaskBaseValues = (id: string, values: Record<string, unknown>): void => {
  taskBaseValues.set(id, { ...values })
}
export const getTaskBaseValues = (id: string): Record<string, unknown> | null =>
  taskBaseValues.get(id) ?? null
